import { DocumentV2, photoTypeRegex, videoTypeRegex } from '@eigtech/documents-types'
import {
  UpdateDocumentRequest,
  useGetDocumentUrl,
  useUpdateDocument,
} from '@eigtech/ui-shared-documents'
import { createForm, FormDrawer } from '@eigtech/ui-shared-forms/shadcn'
import { getLog } from '@eigtech/ui-shared-logging'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Button } from '@eigtech/ui/components/button'
import { ButtonGroup } from '@eigtech/ui/components/button-group'
import { SheetActions, SheetProps } from '@eigtech/ui/components/sheet'
import { toast } from '@eigtech/ui/components/sonner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { RotateCcw, RotateCw, TriangleAlert } from 'lucide-react'
import { useImperativeHandle, useRef } from 'react'
import { z } from 'zod'
import { MediaPreview } from './MediaPreview'
import { useRotatePhotoState } from './useRotatePhotoState'

const log = getLog()

export type UpdateDocumentDrawerProps = SheetProps & {
  document: DocumentV2
}

export function UpdateDocumentDrawer({ document, ...props }: UpdateDocumentDrawerProps) {
  const localActionsRef = useRef<SheetActions>(null)
  useImperativeHandle(props.actionsRef, () => ({
    close: localActionsRef.current?.close ?? (() => log.warn('cannot close yet, not mounted')),
    unmount:
      localActionsRef.current?.unmount ?? (() => log.warn('cannot unmount yet, not mounted')),
  }))

  const { data: url, isFetching } = useGetDocumentUrl({
    entityId: document.entityId,
    documentId: document.id,
  })

  const form = useForm({
    defaultValues: {
      title: document?.title ?? '',
      notes: document?.notes ?? '',
      description: document?.description ?? '',
    },
  })

  const { mutateAsync: updateDocument, isError } = useUpdateDocument()

  async function handleSubmit({ notes, ...data }: UpdateDocumentForm) {
    // the form schema requires these fields at runtime, but zod infers them as
    // optional when strictNullChecks is off
    const payload: UpdateDocumentRequest = {
      entityId: document.entityId,
      documentId: document.id,
      notes,
      ...data,
    }

    await updateDocument(payload)

    toast.success('Successfully updated document information!')

    localActionsRef.current?.close()
  }

  const {
    rotationDegrees,
    saveRotateEnabled,
    saveRotate,
    rotateLeft,
    rotateRight,
    isPending: isPendingRotatePhoto,
    isError: isRotatePhotoError,
  } = useRotatePhotoState(document)

  return (
    <FormDrawer
      {...props}
      actionsRef={localActionsRef}
      form={form}
      title="Update Document Information"
      onSubmit={handleSubmit}
    >
      {!!url && (
        <div className="flex flex-col items-center gap-4 rounded-lg border p-4">
          <Tooltip>
            <TooltipTrigger render={<div className="flex items-center justify-center" />}>
              {document.filename.match(videoTypeRegex) ? (
                <video className="w-full" src={url} />
              ) : document.filename?.match(photoTypeRegex) ? (
                <a className="relative h-[30vh] w-[30vh]" href={url} target={document.filename}>
                  <MediaPreview
                    className="rotate-(--media-rotate) h-full w-full"
                    document={document}
                    style={
                      {
                        '--media-rotate': `${rotationDegrees ?? 0}deg`,
                      } as React.CSSProperties
                    }
                  />
                </a>
              ) : (
                <Button render={<a href={url} target={document.filename} />} variant="outline">
                  Open Document Preview
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <span>{document.filename}</span>
            </TooltipContent>
          </Tooltip>

          {document.filename.match(photoTypeRegex) && (
            <>
              {isRotatePhotoError && (
                <Alert
                  data-document-id={document.id}
                  data-slot="rotate-error"
                  variant="destructive"
                >
                  <AlertIcon>
                    <TriangleAlert />
                  </AlertIcon>
                  <AlertContent>
                    <AlertTitle>Could not save rotation</AlertTitle>
                    <AlertDescription>
                      Something went wrong Please try again. If the issue persists, contact EIG Tech
                      Support.
                    </AlertDescription>
                  </AlertContent>
                </Alert>
              )}

              <ButtonGroup size="sm" variant="outline">
                <Button
                  data-document-id={document.id}
                  data-slot="rotate-left"
                  type="button"
                  onClick={rotateLeft}
                >
                  <RotateCcw /> Rotate Left
                </Button>
                <Button
                  data-document-id={document.id}
                  data-slot="save-rotation"
                  disabled={!saveRotateEnabled}
                  isLoading={isPendingRotatePhoto || isFetching}
                  type="button"
                  variant="primary"
                  onClick={saveRotate}
                >
                  Save
                </Button>
                <Button
                  data-document-id={document.id}
                  data-slot="rotate-right"
                  type="button"
                  onClick={rotateRight}
                >
                  Rotate Right <RotateCw />
                </Button>
              </ButtonGroup>
            </>
          )}
        </div>
      )}

      <InputField label="Title" name="title" />
      <InputField label="Notes" name="notes" />
      <TextareaField label="Description" name="description" />

      {isError && (
        <Alert variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>
              We could not update this file&apos;s information. Please try again. If the issue
              persists, please contact EIG Customer Service.
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}
    </FormDrawer>
  )
}

export const UpdateDocumentSchema = z.object({
  title: z.string().max(50, 'String cannot contain more than 50 characters'),
  notes: z.string().min(1).max(50, 'String cannot contain more than 50 characters'),
  description: z.string().max(500, 'String cannot contain more than 500 characters'),
})

type UpdateDocumentForm = z.infer<typeof UpdateDocumentSchema>

const { useForm, InputField, TextareaField } = createForm(
  UpdateDocumentSchema,
  'updateDocumentForm'
)
